import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, X, Loader2, ImageIcon, Film, Link2, MessageCircle, Send, Mail, Users, Download, Clock, Phone, Truck, Package, Tag, MapPin, CheckCircle2, XCircle, ChevronDown, ChevronUp, Copy, ExternalLink, Paperclip } from "lucide-react";
import { api, API } from "@/lib/api";
import { MarkdownEditor } from "@/components/Markdown";

const GalleryField = ({ value = [], onChange }) => {
  const [uploading, setUploading] = useState(false);
  const add = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const ids = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/content-image", fd);
        ids.push(data.file_id);
      }
      onChange([...(value || []), ...ids]);
      toast.success(`${ids.length} image${ids.length !== 1 ? "s" : ""} added`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div>
      <label className={labelCls}>Gallery Images</label>
      <div className="flex flex-wrap gap-3">
        {(value || []).map((id, i) => (
          <div key={i} className="relative w-20 h-20 bg-slate-100 overflow-hidden group">
            <img src={`${API}/public/files/${id}`} alt="" className="w-full h-full object-cover" />
            <button type="button" data-testid={`gallery-remove-${i}`} onClick={() => onChange(value.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-[#D90429] text-white p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <label className="w-20 h-20 border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-[#D90429] transition-colors" data-testid="gallery-add-label">
          {uploading ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : <Plus className="w-5 h-5 text-slate-400" />}
          <input type="file" accept="image/*" multiple onChange={add} className="hidden" data-testid="gallery-input" />
        </label>
      </div>
    </div>
  );
};

const BulletsField = ({ value = [], onChange }) => (
  <div>
    <label className={labelCls}>"What We Do" Bullets</label>
    <div className="space-y-2">
      {(value || []).map((b, i) => (
        <div key={i} className="flex gap-2">
          <input data-testid={`bullet-input-${i}`} value={b} onChange={(e) => onChange(value.map((v, j) => (j === i ? e.target.value : v)))} className={inputCls} placeholder={`Point ${i + 1}`} />
          <button type="button" data-testid={`bullet-remove-${i}`} onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-[#D90429] px-2 hover:opacity-70"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button type="button" data-testid="bullet-add" onClick={() => onChange([...(value || []), ""])} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#0A1128] hover:text-[#D90429]"><Plus className="w-3.5 h-3.5" /> Add Point</button>
    </div>
  </div>
);

const DIVISION_ICON_NAMES = ["Shield", "GraduationCap", "Church", "Flag", "Home", "Ticket", "Users", "Landmark", "Building2", "Star", "Heart", "Globe"];
const EVENT_TYPES = ["Rally", "Training", "Workshop", "Community", "Summit"];

const inputCls = "w-full border border-slate-300 px-4 py-2.5 rounded-none focus:outline-none focus:border-[#D90429] transition-colors duration-200";
const labelCls = "block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-1.5";

const ImageField = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/content-image", fd);
      onChange(data.file_id);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <label className={labelCls}>Image (optional)</label>
      <div className="flex items-center gap-4">
        <div className="w-24 h-24 bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
          {value ? <img src={`${API}/public/files/${value}`} alt="preview" className="w-full h-full object-cover" /> : <ImageIcon className="w-7 h-7 text-slate-300" />}
        </div>
        <div className="flex flex-col gap-2">
          <label className="inline-flex items-center gap-2 bg-[#0A1128] text-white px-4 py-2 uppercase tracking-[0.1em] text-xs font-bold cursor-pointer hover:bg-[#1a2547] transition-colors duration-200">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {value ? "Replace" : "Upload"}
            <input type="file" accept="image/*" onChange={upload} className="hidden" data-testid="content-image-input" />
          </label>
          {value && <button type="button" onClick={() => onChange(null)} className="text-xs text-[#D90429] uppercase hover:underline">Remove image</button>}
        </div>
      </div>
    </div>
  );
};

export function DivisionsManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/divisions").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const blank = { title: "", icon: "Shield", text: "", image_file_id: null, hero_image_file_id: null, tagline: "", body: "", bullets: [], gallery: [], order: items.length };

  const save = async () => {
    if (!editing.title.trim() || !editing.text.trim()) { toast.error("Title and short description are required."); return; }
    setSaving(true);
    try {
      const body = {
        title: editing.title, icon: editing.icon, text: editing.text,
        image_file_id: editing.image_file_id, hero_image_file_id: editing.hero_image_file_id,
        tagline: editing.tagline || "", body: editing.body || "",
        bullets: (editing.bullets || []).filter((b) => b.trim()), gallery: editing.gallery || [],
        order: Number(editing.order) || 0,
      };
      if (editing.id) await api.put(`/admin/divisions/${editing.id}`, body);
      else await api.post("/admin/divisions", body);
      toast.success("Division saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this division?")) return;
    try { await api.delete(`/admin/divisions/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="divisions-manager">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <p className="text-slate-500 text-sm">Add, edit, reorder, and illustrate your organization's divisions.</p>
        <button data-testid="add-division-button" onClick={() => setEditing(blank)} className="inline-flex items-center gap-2 bg-[#D90429] text-white px-6 py-3 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#b0031f] transition-colors duration-200"><Plus className="w-4 h-4" /> Add Division</button>
      </div>

      {editing && (
        <div data-testid="division-form" className="bg-white border-2 border-[#0A1128] p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-display text-xl uppercase">{editing.id ? "Edit" : "New"} Division</h3>
            <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-[#D90429]"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Title</label><input data-testid="division-title" className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
            <div><label className={labelCls}>Icon</label><select data-testid="division-icon" className={inputCls} value={editing.icon} onChange={(e) => setEditing({ ...editing, icon: e.target.value })}>{DIVISION_ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}</select></div>
            <div className="sm:col-span-2"><label className={labelCls}>Tagline (short line under the title)</label><input data-testid="division-tagline" className={inputCls} value={editing.tagline || ""} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })} placeholder="e.g. Trained guardians protecting our classrooms." /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Short Description (card summary)</label><textarea data-testid="division-text" rows={2} className={`${inputCls} resize-none`} value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Full Page Content</label><MarkdownEditor testid="division-body" value={editing.body || ""} onChange={(v) => setEditing({ ...editing, body: v })} /></div>
            <div className="sm:col-span-2"><BulletsField value={editing.bullets || []} onChange={(v) => setEditing({ ...editing, bullets: v })} /></div>
            <div><label className={labelCls}>Card Image</label><ImageField value={editing.image_file_id} onChange={(id) => setEditing({ ...editing, image_file_id: id })} /></div>
            <div><label className={labelCls}>Hero Banner (page top)</label><ImageField value={editing.hero_image_file_id} onChange={(id) => setEditing({ ...editing, hero_image_file_id: id })} /></div>
            <div className="sm:col-span-2"><GalleryField value={editing.gallery || []} onChange={(v) => setEditing({ ...editing, gallery: v })} /></div>
            <div><label className={labelCls}>Order</label><input type="number" className={inputCls} value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></div>
          </div>
          <button data-testid="save-division-button" disabled={saving} onClick={save} className="mt-5 bg-[#0A1128] text-white px-8 py-3 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#1a2547] transition-colors duration-200 disabled:opacity-60 inline-flex items-center gap-2">{saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save Division"}</button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((d) => (
          <div key={d.id} data-testid="division-manage-row" className="bg-white border border-slate-200 p-5 flex gap-4">
            <div className="w-16 h-16 bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
              {d.image_file_id ? <img src={`${API}/public/files/${d.image_file_id}`} alt={d.title} className="w-full h-full object-cover" /> : <span className="text-[10px] uppercase text-slate-400 text-center px-1">{d.icon}</span>}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-display text-lg uppercase leading-tight">{d.title}</h4>
              <p className="text-sm text-slate-500 line-clamp-2 mt-1">{d.text}</p>
              <div className="flex gap-3 mt-2 flex-wrap items-center">
                <button data-testid={`edit-division-${d.id}`} onClick={() => setEditing(d)} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#0A1128] hover:text-[#D90429]"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                {d.slug && <a data-testid={`view-division-${d.id}`} href={`/divisions/${d.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#0A1128] hover:text-[#D90429]"><ExternalLink className="w-3.5 h-3.5" /> Page</a>}
                <button data-testid={`delete-division-${d.id}`} onClick={() => remove(d.id)} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#D90429] hover:opacity-70"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function EventsManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/events").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const blank = { title: "", type: "Rally", month: "", day: "", time: "", location: "", desc: "", image_file_id: null, order: items.length };

  const save = async () => {
    if (!editing.title.trim() || !editing.month.trim() || !editing.day.trim()) { toast.error("Title, month and day are required."); return; }
    setSaving(true);
    try {
      const body = { title: editing.title, type: editing.type, month: editing.month.toUpperCase(), day: editing.day, time: editing.time, location: editing.location, desc: editing.desc, image_file_id: editing.image_file_id, order: Number(editing.order) || 0 };
      if (editing.id) await api.put(`/admin/events/${editing.id}`, body);
      else await api.post("/admin/events", body);
      toast.success("Event saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try { await api.delete(`/admin/events/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="events-manager">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <p className="text-slate-500 text-sm">Create and manage upcoming events shown on the public Events page.</p>
        <button data-testid="add-event-button" onClick={() => setEditing(blank)} className="inline-flex items-center gap-2 bg-[#D90429] text-white px-6 py-3 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#b0031f] transition-colors duration-200"><Plus className="w-4 h-4" /> Add Event</button>
      </div>

      {editing && (
        <div data-testid="event-form" className="bg-white border-2 border-[#0A1128] p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-display text-xl uppercase">{editing.id ? "Edit" : "New"} Event</h3>
            <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-[#D90429]"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><label className={labelCls}>Title</label><input data-testid="event-title" className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
            <div><label className={labelCls}>Type</label><select data-testid="event-type" className={inputCls} value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })}>{EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Month</label><input data-testid="event-month" placeholder="JUL" className={inputCls} value={editing.month} onChange={(e) => setEditing({ ...editing, month: e.target.value })} /></div>
              <div><label className={labelCls}>Day</label><input data-testid="event-day" placeholder="12" className={inputCls} value={editing.day} onChange={(e) => setEditing({ ...editing, day: e.target.value })} /></div>
            </div>
            <div><label className={labelCls}>Time</label><input placeholder="10:00 AM – 3:00 PM" className={inputCls} value={editing.time} onChange={(e) => setEditing({ ...editing, time: e.target.value })} /></div>
            <div><label className={labelCls}>Location</label><input placeholder="City, ST" className={inputCls} value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Description</label><textarea rows={3} className={`${inputCls} resize-none`} value={editing.desc} onChange={(e) => setEditing({ ...editing, desc: e.target.value })} /></div>
            <div><label className={labelCls}>Order</label><input type="number" className={inputCls} value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></div>
            <div className="sm:col-span-2"><ImageField value={editing.image_file_id} onChange={(id) => setEditing({ ...editing, image_file_id: id })} /></div>
          </div>
          <button data-testid="save-event-button" disabled={saving} onClick={save} className="mt-5 bg-[#0A1128] text-white px-8 py-3 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#1a2547] transition-colors duration-200 disabled:opacity-60 inline-flex items-center gap-2">{saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save Event"}</button>
        </div>
      )}

      <div className="space-y-3">
        {items.map((e) => (
          <div key={e.id} data-testid="event-manage-row" className="bg-white border border-slate-200 p-4 flex items-center gap-4">
            <div className="bg-[#0A1128] text-white text-center py-2 w-[64px] shrink-0">
              <div className="text-[10px] uppercase tracking-widest text-[#F4A261]">{e.month}</div>
              <div className="font-display text-2xl leading-none">{e.day}</div>
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-display text-lg uppercase leading-tight">{e.title}</h4>
              <p className="text-sm text-slate-500 truncate">{e.type} · {e.location}</p>
            </div>
            <div className="flex gap-3 shrink-0">
              <button data-testid={`edit-event-${e.id}`} onClick={() => setEditing(e)} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#0A1128] hover:text-[#D90429]"><Pencil className="w-3.5 h-3.5" /> Edit</button>
              <button data-testid={`delete-event-${e.id}`} onClick={() => remove(e.id)} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#D90429] hover:opacity-70"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


const MEDIA_TYPES = [
  ["video_link", "Video Link (YouTube / Vimeo)"],
  ["video_upload", "Upload Video File"],
  ["image", "Image / Photo"],
];

const VideoField = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/media-video", fd);
      onChange(data.file_id);
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <label className={labelCls}>Video File (mp4 / webm · max 100MB)</label>
      <div className="flex items-center gap-4">
        <div className="w-24 h-24 bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
          <Film className={`w-7 h-7 ${value ? "text-[#0A1128]" : "text-slate-300"}`} />
        </div>
        <div className="flex flex-col gap-2">
          <label className="inline-flex items-center gap-2 bg-[#0A1128] text-white px-4 py-2 uppercase tracking-[0.1em] text-xs font-bold cursor-pointer hover:bg-[#1a2547] transition-colors duration-200">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {value ? "Replace" : "Upload Video"}
            <input type="file" accept="video/*" onChange={upload} className="hidden" data-testid="media-video-input" />
          </label>
          {value && <span className="text-xs text-green-600 uppercase tracking-wide font-semibold">Video attached ✓</span>}
        </div>
      </div>
    </div>
  );
};

function ytId(url = "") {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
}

function mediaThumb(m) {
  if (m.thumbnail_file_id) return `${API}/public/files/${m.thumbnail_file_id}`;
  if (m.media_type === "image" && m.file_id) return `${API}/public/files/${m.file_id}`;
  if (m.media_type === "video_link" && m.video_url) {
    const id = ytId(m.video_url);
    if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
  }
  return null;
}

export function MediaManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [moderating, setModerating] = useState(null);
  const [modComments, setModComments] = useState([]);

  const load = () => api.get("/media").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const blank = { title: "", description: "", tag: "", media_type: "video_link", video_url: "", file_id: null, thumbnail_file_id: null, order: items.length, comments_enabled: true };

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Title is required."); return; }
    if (editing.media_type === "video_link" && !editing.video_url.trim()) { toast.error("Please paste a video link."); return; }
    if ((editing.media_type === "video_upload" || editing.media_type === "image") && !editing.file_id) {
      toast.error(`Please upload a ${editing.media_type === "image" ? "image" : "video"} file.`); return;
    }
    setSaving(true);
    try {
      const body = {
        title: editing.title, description: editing.description, tag: editing.tag,
        media_type: editing.media_type, video_url: editing.video_url || null,
        file_id: editing.file_id, thumbnail_file_id: editing.thumbnail_file_id,
        order: Number(editing.order) || 0, comments_enabled: editing.comments_enabled !== false,
      };
      if (editing.id) await api.put(`/admin/media-posts/${editing.id}`, body);
      else await api.post("/admin/media-posts", body);
      toast.success("Media saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this media post?")) return;
    try { await api.delete(`/admin/media-posts/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const startEdit = (m) => setEditing({ ...m, video_url: m.video_url || "" });

  const toggleModerate = (id) => {
    if (moderating === id) { setModerating(null); setModComments([]); return; }
    setModerating(id);
    api.get(`/media/${id}/comments`).then(({ data }) => setModComments(data)).catch(() => setModComments([]));
  };

  const deleteComment = async (cid) => {
    try {
      await api.delete(`/media-comments/${cid}`);
      setModComments((cs) => cs.filter((c) => c.id !== cid));
      toast.success("Comment removed");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="media-manager">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <p className="text-slate-500 text-sm">Post videos and photos to your public Media page. Paste a YouTube/Vimeo link or upload a file directly.</p>
        <button data-testid="add-media-button" onClick={() => setEditing(blank)} className="inline-flex items-center gap-2 bg-[#D90429] text-white px-6 py-3 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#b0031f] transition-colors duration-200"><Plus className="w-4 h-4" /> Add Media</button>
      </div>

      {editing && (
        <div data-testid="media-form" className="bg-white border-2 border-[#0A1128] p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-display text-xl uppercase">{editing.id ? "Edit" : "New"} Media Post</h3>
            <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-[#D90429]"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><label className={labelCls}>Title</label><input data-testid="media-title" className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
            <div><label className={labelCls}>Media Type</label><select data-testid="media-type" className={inputCls} value={editing.media_type} onChange={(e) => setEditing({ ...editing, media_type: e.target.value, file_id: null, video_url: "" })}>{MEDIA_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            <div><label className={labelCls}>Category / Tag</label><input data-testid="media-tag" placeholder="e.g. Training, Events, News" className={inputCls} value={editing.tag} onChange={(e) => setEditing({ ...editing, tag: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Description</label><textarea data-testid="media-description" rows={3} className={`${inputCls} resize-none`} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>

            {editing.media_type === "video_link" && (
              <div className="sm:col-span-2"><label className={labelCls}>Video Link</label><input data-testid="media-video-url" placeholder="https://youtube.com/watch?v=…  or  https://vimeo.com/…" className={inputCls} value={editing.video_url} onChange={(e) => setEditing({ ...editing, video_url: e.target.value })} /></div>
            )}
            {editing.media_type === "video_upload" && (
              <div className="sm:col-span-2"><VideoField value={editing.file_id} onChange={(id) => setEditing({ ...editing, file_id: id })} /></div>
            )}
            {editing.media_type === "image" && (
              <div className="sm:col-span-2"><ImageField value={editing.file_id} onChange={(id) => setEditing({ ...editing, file_id: id })} /></div>
            )}

            {editing.media_type !== "image" && (
              <div className="sm:col-span-2">
                <label className={labelCls}>Thumbnail (optional — shown before the video plays)</label>
                <ImageField value={editing.thumbnail_file_id} onChange={(id) => setEditing({ ...editing, thumbnail_file_id: id })} />
              </div>
            )}
            <div><label className={labelCls}>Order</label><input type="number" className={inputCls} value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></div>
            <div className="sm:col-span-2">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input data-testid="media-comments-toggle" type="checkbox" checked={editing.comments_enabled !== false} onChange={(e) => setEditing({ ...editing, comments_enabled: e.target.checked })} className="w-4 h-4 accent-[#D90429]" />
                <span className="text-sm text-slate-700">Allow members to comment on this post</span>
              </label>
            </div>
          </div>
          <button data-testid="save-media-button" disabled={saving} onClick={save} className="mt-5 bg-[#0A1128] text-white px-8 py-3 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#1a2547] transition-colors duration-200 disabled:opacity-60 inline-flex items-center gap-2">{saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save Media"}</button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && <p className="text-slate-400 py-12 text-center bg-white border border-slate-200 sm:col-span-2 lg:col-span-3">No media posted yet.</p>}
        {items.map((m) => {
          const thumb = mediaThumb(m);
          return (
            <div key={m.id} data-testid="media-manage-row" className="bg-white border border-slate-200 overflow-hidden flex flex-col">
              <div className="h-36 bg-slate-100 flex items-center justify-center overflow-hidden relative">
                {thumb ? <img src={thumb} alt={m.title} className="w-full h-full object-cover" /> : (m.media_type === "image" ? <ImageIcon className="w-8 h-8 text-slate-300" /> : <Film className="w-8 h-8 text-slate-300" />)}
                <span className="absolute top-2 left-2 bg-[#0A1128] text-white text-[10px] uppercase tracking-wider px-2 py-1 inline-flex items-center gap-1">
                  {m.media_type === "image" ? <ImageIcon className="w-3 h-3" /> : m.media_type === "video_link" ? <Link2 className="w-3 h-3" /> : <Film className="w-3 h-3" />}
                  {m.media_type === "image" ? "Image" : "Video"}
                </span>
              </div>
              <div className="p-4 flex-1 flex flex-col">
                {m.tag && <span className="text-[10px] uppercase tracking-[0.15em] text-[#D90429] font-bold mb-1">{m.tag}</span>}
                <h4 className="font-display text-base uppercase leading-tight">{m.title}</h4>
                <p className="text-xs text-slate-500 line-clamp-2 mt-1 flex-1">{m.description}</p>
                <div className="flex gap-3 mt-3">
                  <button data-testid={`edit-media-${m.id}`} onClick={() => startEdit(m)} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#0A1128] hover:text-[#D90429]"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                  <button data-testid={`moderate-media-${m.id}`} onClick={() => toggleModerate(m.id)} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#0A1128] hover:text-[#D90429]"><MessageCircle className="w-3.5 h-3.5" /> Comments</button>
                  <button data-testid={`delete-media-${m.id}`} onClick={() => remove(m.id)} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#D90429] hover:opacity-70"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                </div>
                {moderating === m.id && (
                  <div data-testid="media-moderation" className="mt-3 border-t border-slate-100 pt-3">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">
                      {m.comments_enabled === false ? "Comments are OFF · " : ""}{modComments.length} comment{modComments.length === 1 ? "" : "s"}
                    </p>
                    {modComments.length === 0 ? (
                      <p className="text-xs text-slate-400">No comments.</p>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {modComments.map((c) => (
                          <div key={c.id} data-testid="mod-comment" className="flex items-start gap-2 bg-slate-50 p-2">
                            <div className="flex-1 min-w-0">
                              <span className="text-[11px] font-semibold text-[#0A1128]">{c.user_name}</span>
                              {c.is_admin && <span className="ml-1 text-[8px] uppercase bg-[#0A1128] text-white px-1 py-0.5">Team</span>}
                              <p className="text-xs text-slate-600 break-words">{c.text}</p>
                            </div>
                            <button data-testid={`mod-delete-${c.id}`} onClick={() => deleteComment(c.id)} className="text-slate-300 hover:text-[#D90429] shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const AUDIENCE_OPTIONS = [
  ["everyone", "Everyone (subscribers + members + approved applicants)"],
  ["subscribers", "Newsletter subscribers"],
  ["members", "All members"],
  ["applicants", "Approved applicants"],
];

export function BroadcastManager() {
  const [audience, setAudience] = useState("everyone");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [donate, setDonate] = useState(true);
  const [counts, setCounts] = useState({ subscribers: 0, members: 0, applicants: 0, everyone: 0 });
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [scheduleMode, setScheduleMode] = useState("now"); // "now" | "later"
  const [scheduledAt, setScheduledAt] = useState("");
  const [exporting, setExporting] = useState(false);
  const [subStats, setSubStats] = useState({ total: 0, confirmed: 0, pending: 0 });
  const [files, setFiles] = useState([]); // {id, filename, content_type, is_image, url}
  const [uploading, setUploading] = useState(false);

  const onFiles = async (e) => {
    const chosen = Array.from(e.target.files || []);
    e.target.value = "";
    if (!chosen.length) return;
    setUploading(true);
    try {
      for (const f of chosen) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/broadcast/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => [...prev, data]);
      }
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };
  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const loadCounts = () => api.get("/admin/broadcast/audience").then(({ data }) => setCounts(data)).catch(() => {});
  const loadHistory = () => api.get("/admin/broadcasts").then(({ data }) => setHistory(data)).catch(() => {});
  const loadSubStats = () => api.get("/admin/newsletter/stats").then(({ data }) => setSubStats(data)).catch(() => {});
  useEffect(() => { loadCounts(); loadHistory(); loadSubStats(); }, []);

  const recipientCount = counts[audience] ?? 0;

  const send = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Subject and message are required."); return; }
    const later = scheduleMode === "later";
    if (later && !scheduledAt) { toast.error("Please pick a date and time to schedule."); return; }
    let scheduledIso = null;
    if (later) {
      const dt = new Date(scheduledAt);
      if (isNaN(dt.getTime())) { toast.error("Invalid date/time."); return; }
      if (dt.getTime() < Date.now() + 60000) { toast.error("Please pick a time at least a minute in the future."); return; }
      scheduledIso = dt.toISOString();
    } else if (recipientCount === 0) {
      toast.error("No recipients for the selected audience yet."); return;
    }
    const confirmMsg = later
      ? `Schedule this email to ${audience} for ${new Date(scheduledAt).toLocaleString()}?`
      : `Send this email to ${recipientCount} recipient${recipientCount === 1 ? "" : "s"} now?`;
    if (!window.confirm(confirmMsg)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast", {
        subject, message, audience, include_donate_button: donate, scheduled_at: scheduledIso,
        image_file_ids: files.filter((f) => f.is_image).map((f) => f.id),
        attachment_file_ids: files.filter((f) => !f.is_image).map((f) => f.id),
      });
      if (data.scheduled) {
        toast.success(`Broadcast scheduled for ${new Date(data.scheduled_at).toLocaleString()}.`);
      } else {
        toast.success(`Broadcast queued to ${data.recipient_count} recipient${data.recipient_count === 1 ? "" : "s"}.`);
      }
      setSubject(""); setMessage(""); setScheduledAt(""); setScheduleMode("now"); setFiles([]);
      setTimeout(loadHistory, 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send broadcast");
    } finally { setSending(false); }
  };

  const sendTest = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Add a subject and message first."); return; }
    setTesting(true);
    try {
      const { data } = await api.post("/admin/broadcast/test", {
        subject, message, audience, include_donate_button: donate,
        image_file_ids: files.filter((f) => f.is_image).map((f) => f.id),
        attachment_file_ids: files.filter((f) => !f.is_image).map((f) => f.id),
      });
      toast.success(`Test email sent to ${data.sent_to}. Check your inbox.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send test email");
    } finally { setTesting(false); }
  };

  const cancelScheduled = async (id) => {
    if (!window.confirm("Cancel this scheduled broadcast?")) return;
    try {
      await api.delete(`/admin/broadcasts/${id}`);
      toast.success("Scheduled broadcast canceled");
      loadHistory();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not cancel"); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/newsletter/subscribers/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "patriot-power-subscribers.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Subscribers exported");
    } catch (err) {
      toast.error("Export failed");
    } finally { setExporting(false); }
  };

  return (
    <div data-testid="broadcast-manager">
      <div className="grid lg:grid-cols-[1fr_320px] gap-8">
        {/* Composer */}
        <div>
          <p className="text-slate-500 text-sm mb-6">Write a message and send it to your newsletter subscribers and members. Emails are sent individually — recipients never see each other.</p>

          <div className="bg-white border-2 border-[#0A1128] p-6 space-y-4">
            <div>
              <label className={labelCls}>Audience</label>
              <select data-testid="broadcast-audience" className={inputCls} value={audience} onChange={(e) => setAudience(e.target.value)}>
                {AUDIENCE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <p className="text-xs text-slate-500 mt-1.5" data-testid="broadcast-recipient-count">
                <Users className="w-3.5 h-3.5 inline -mt-0.5 mr-1 text-[#D90429]" />
                {recipientCount} recipient{recipientCount === 1 ? "" : "s"} will receive this email
              </p>
            </div>
            <div>
              <label className={labelCls}>Subject</label>
              <input data-testid="broadcast-subject" className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Join us at Saturday's rally" />
            </div>
            <div>
              <label className={labelCls}>Message</label>
              <textarea data-testid="broadcast-message" rows={8} className={`${inputCls} resize-none`} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Write your message here. Line breaks are preserved." />
            </div>

            <div>
              <label className={labelCls}>Photos &amp; Attachments</label>
              <label data-testid="broadcast-upload-label" className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 hover:border-[#D90429] cursor-pointer px-4 py-4 text-sm text-slate-500 transition-colors duration-200">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "Uploading…" : "Add photos (shown in email) or files (attached)"}
                <input data-testid="broadcast-file-input" type="file" multiple accept="image/*,application/pdf,.doc,.docx" className="hidden" onChange={onFiles} disabled={uploading} />
              </label>
              {files.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-3" data-testid="broadcast-attachments">
                  {files.map((f) => (
                    <div key={f.id} data-testid={`broadcast-file-${f.id}`} className="relative border border-slate-200 bg-slate-50 p-1.5 flex items-center gap-2">
                      {f.is_image ? (
                        <img src={`${API}/public/files/${f.id}`} alt={f.filename} className="w-14 h-14 object-cover" />
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2 py-3 text-xs text-slate-600 max-w-[170px] truncate"><Paperclip className="w-4 h-4 shrink-0 text-[#D90429]" /> {f.filename}</span>
                      )}
                      <button type="button" data-testid={`remove-file-${f.id}`} onClick={() => removeFile(f.id)} className="absolute -top-2 -right-2 bg-[#D90429] text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-[#b0031f]"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input data-testid="broadcast-donate-toggle" type="checkbox" checked={donate} onChange={(e) => setDonate(e.target.checked)} className="w-4 h-4 accent-[#D90429]" />
              <span className="text-sm text-slate-700">Include a "Donate" button at the bottom</span>
            </label>

            <div>
              <label className={labelCls}>When to send</label>
              <div className="flex gap-2">
                <button type="button" data-testid="broadcast-mode-now" onClick={() => setScheduleMode("now")} className={`flex-1 px-4 py-2.5 text-xs uppercase tracking-[0.1em] font-bold border transition-colors duration-200 ${scheduleMode === "now" ? "bg-[#0A1128] text-white border-[#0A1128]" : "bg-white text-slate-500 border-slate-300 hover:border-slate-400"}`}>Send Now</button>
                <button type="button" data-testid="broadcast-mode-later" onClick={() => setScheduleMode("later")} className={`flex-1 px-4 py-2.5 text-xs uppercase tracking-[0.1em] font-bold border transition-colors duration-200 inline-flex items-center justify-center gap-1.5 ${scheduleMode === "later" ? "bg-[#0A1128] text-white border-[#0A1128]" : "bg-white text-slate-500 border-slate-300 hover:border-slate-400"}`}><Clock className="w-3.5 h-3.5" /> Schedule</button>
              </div>
              {scheduleMode === "later" && (
                <input type="datetime-local" data-testid="broadcast-schedule-input" className={`${inputCls} mt-3`} value={scheduledAt} min={new Date(Date.now() + 60000 - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)} onChange={(e) => setScheduledAt(e.target.value)} />
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button data-testid="broadcast-send-button" disabled={sending} onClick={send} className="bg-[#D90429] text-white px-8 py-3 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#b0031f] transition-colors duration-200 disabled:opacity-60 inline-flex items-center gap-2">
                {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Working…</> : scheduleMode === "later" ? <><Clock className="w-4 h-4" /> Schedule Broadcast</> : <><Send className="w-4 h-4" /> Send Broadcast</>}
              </button>
              <button data-testid="broadcast-test-button" disabled={testing || sending} onClick={sendTest} className="border-2 border-[#0A1128] text-[#0A1128] px-6 py-3 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#0A1128] hover:text-white transition-colors duration-200 disabled:opacity-60 inline-flex items-center gap-2">
                {testing ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Mail className="w-4 h-4" /> Send Test To Myself</>}
              </button>
            </div>
          </div>
        </div>

        {/* Side: stats + history */}
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display text-sm uppercase text-slate-500">Your Audience</h4>
              <button data-testid="export-subscribers-button" onClick={exportCsv} disabled={exporting || counts.subscribers === 0} title="Download subscribers as CSV" className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#0A1128] hover:text-[#D90429] disabled:opacity-40">
                {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} CSV
              </button>
            </div>
            <div className="space-y-2 text-sm" data-testid="subscriber-stats">
              <div className="flex justify-between"><span className="text-slate-500 inline-flex items-center gap-1.5"><Mail className="w-4 h-4" /> Subscribers (confirmed)</span><span className="font-bold text-[#0A1128]" data-testid="stat-confirmed">{subStats.confirmed}</span></div>
              <div className="flex justify-between"><span className="text-slate-400 inline-flex items-center gap-1.5 pl-5"><Clock className="w-3.5 h-3.5" /> Pending confirmation</span><span className="font-bold text-[#F4A261]" data-testid="stat-pending">{subStats.pending}</span></div>
              <div className="flex justify-between"><span className="text-slate-500 inline-flex items-center gap-1.5"><Users className="w-4 h-4" /> Members</span><span className="font-bold text-[#0A1128]">{counts.members}</span></div>
              <div className="flex justify-between border-t border-slate-100 pt-2"><span className="text-slate-500">Everyone (unique)</span><span className="font-bold text-[#D90429]">{counts.everyone}</span></div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-5">
            <h4 className="font-display text-sm uppercase text-slate-500 mb-3">Recent Broadcasts</h4>
            {history.length === 0 ? (
              <p className="text-xs text-slate-400">No broadcasts sent yet.</p>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {history.map((b) => (
                  <div key={b.id} data-testid="broadcast-history-row" className="border-b border-slate-100 pb-2 last:border-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-[#0A1128] truncate flex-1">{b.subject}</p>
                      {b.status === "scheduled" && (
                        <button data-testid={`cancel-broadcast-${b.id}`} onClick={() => cancelScheduled(b.id)} className="text-slate-300 hover:text-[#D90429] shrink-0" title="Cancel scheduled send"><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">
                      {b.audience}
                      {b.status === "scheduled"
                        ? <> · <span className="text-blue-600 font-semibold">scheduled</span></>
                        : <> · {b.sent_count}/{b.recipient_count} sent · <span className={b.status === "sent" ? "text-green-600" : "text-[#F4A261]"}>{b.status}</span></>}
                    </p>
                    <p className="text-[10px] text-slate-400 inline-flex items-center gap-1">
                      {b.status === "scheduled" && b.scheduled_at
                        ? <><Clock className="w-3 h-3" /> for {new Date(b.scheduled_at).toLocaleString()}</>
                        : new Date(b.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


const SCHOOL_STATUSES = ["Planned", "Planning — Opening 2026", "Planning — Opening 2027", "Fundraising", "Site Selection", "Enrolling Now", "Open"];
const SCHOOL_LEVELS = ["Elementary School", "Middle School", "High School"];

export function SchoolsManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/schools").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const blank = { name: "", level: "Elementary School", location: "", status: "Planned", description: "", grade_levels: "", contact_email: "", image_file_id: null, order: items.length };

  const save = async () => {
    if (!editing.name.trim()) { toast.error("School name is required."); return; }
    setSaving(true);
    try {
      const body = {
        name: editing.name, level: editing.level, location: editing.location, status: editing.status,
        description: editing.description, grade_levels: editing.grade_levels,
        contact_email: editing.contact_email, image_file_id: editing.image_file_id,
        order: Number(editing.order) || 0,
      };
      if (editing.id) await api.put(`/admin/schools/${editing.id}`, body);
      else await api.post("/admin/schools", body);
      toast.success("School saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this school?")) return;
    try { await api.delete(`/admin/schools/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="schools-manager">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <p className="text-slate-500 text-sm">Add and manage the planned schools shown on the public United Patriot Schools page.</p>
        <button data-testid="add-school-button" onClick={() => setEditing(blank)} className="inline-flex items-center gap-2 bg-[#D90429] text-white px-6 py-3 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#b0031f] transition-colors duration-200"><Plus className="w-4 h-4" /> Add School</button>
      </div>

      {editing && (
        <div data-testid="school-form" className="bg-white border-2 border-[#0A1128] p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-display text-xl uppercase">{editing.id ? "Edit" : "New"} School</h3>
            <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-[#D90429]"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><label className={labelCls}>School Name</label><input data-testid="school-name" className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
            <div><label className={labelCls}>School Level</label><select data-testid="school-level" className={inputCls} value={editing.level} onChange={(e) => setEditing({ ...editing, level: e.target.value })}>{SCHOOL_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}</select></div>
            <div><label className={labelCls}>Location (City, State)</label><input data-testid="school-location" placeholder="Dallas, TX" className={inputCls} value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} /></div>
            <div><label className={labelCls}>Status</label><select data-testid="school-status" className={inputCls} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>{SCHOOL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
            <div><label className={labelCls}>Grade Levels</label><input data-testid="school-grades" placeholder="K–5" className={inputCls} value={editing.grade_levels} onChange={(e) => setEditing({ ...editing, grade_levels: e.target.value })} /></div>
            <div><label className={labelCls}>Contact Email</label><input data-testid="school-email" type="email" placeholder="info@school.org" className={inputCls} value={editing.contact_email} onChange={(e) => setEditing({ ...editing, contact_email: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Description</label><textarea data-testid="school-description" rows={4} className={`${inputCls} resize-none`} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
            <div><label className={labelCls}>Order</label><input type="number" className={inputCls} value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></div>
            <div className="sm:col-span-2"><ImageField value={editing.image_file_id} onChange={(id) => setEditing({ ...editing, image_file_id: id })} /></div>
          </div>
          <button data-testid="save-school-button" disabled={saving} onClick={save} className="mt-5 bg-[#0A1128] text-white px-8 py-3 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#1a2547] transition-colors duration-200 disabled:opacity-60 inline-flex items-center gap-2">{saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save School"}</button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {items.length === 0 && <p className="text-slate-400 py-12 text-center bg-white border border-slate-200 sm:col-span-2">No schools added yet.</p>}
        {items.map((s) => (
          <div key={s.id} data-testid="school-manage-row" className="bg-white border border-slate-200 p-5 flex gap-4">
            <div className="w-16 h-16 bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
              {s.image_file_id ? <img src={`${API}/public/files/${s.image_file_id}`} alt={s.name} className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-slate-300" />}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-display text-lg uppercase leading-tight">{s.name}</h4>
              <p className="text-xs uppercase tracking-wide text-[#D90429] font-bold mt-0.5">{s.level ? `${s.level} · ` : ""}{s.status}</p>
              <p className="text-sm text-slate-500 truncate">{s.location}{s.grade_levels ? ` · ${s.grade_levels}` : ""}</p>
              <div className="flex gap-3 mt-2">
                <button data-testid={`edit-school-${s.id}`} onClick={() => setEditing(s)} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#0A1128] hover:text-[#D90429]"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                <button data-testid={`delete-school-${s.id}`} onClick={() => remove(s.id)} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#D90429] hover:opacity-70"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


export function EnrollmentManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/admin/enrollments").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try { await api.delete(`/admin/enrollments/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="enrollment-manager">
      <p className="text-slate-500 text-sm mb-6">Parents who registered interest in your United Patriot Schools. New leads also email you automatically.</p>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
      ) : items.length === 0 ? (
        <p className="text-slate-400 py-12 text-center bg-white border border-slate-200">No enrollment leads yet.</p>
      ) : (
        <div className="space-y-3">
          {items.map((e) => (
            <div key={e.id} data-testid="enrollment-row" className="bg-white border border-slate-200 p-5 flex gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-display text-lg uppercase leading-tight">{e.parent_name}</h4>
                  <span className="text-[10px] uppercase tracking-[0.15em] bg-[#0A1128] text-white px-2 py-0.5">{e.school_name}</span>
                  {e.student_grade && <span className="text-xs text-slate-500">Grade: {e.student_grade}</span>}
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1.5 text-sm text-slate-600">
                  <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1.5 hover:text-[#D90429]"><Mail className="w-3.5 h-3.5 text-[#D90429]" /> {e.email}</a>
                  {e.phone && <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#D90429]"><Phone className="w-3.5 h-3.5 text-[#D90429]" /> {e.phone}</a>}
                </div>
                {e.message && <p className="text-sm text-slate-500 mt-2 italic">"{e.message}"</p>}
                <p className="text-[10px] text-slate-400 mt-2">{new Date(e.created_at).toLocaleString()}</p>
              </div>
              <button data-testid={`delete-enrollment-${e.id}`} onClick={() => remove(e.id)} className="text-slate-300 hover:text-[#D90429] shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export function StoreManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/products").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const blank = { name: "", description: "", price: "", image_file_id: null, sizes: "", colors: "", stock: "", requires_shipping: true, active: true, order: items.length, godaddy_url: "" };

  const startEdit = (p) => setEditing({ ...p, price: String(p.price), sizes: (p.sizes || []).join(", "), colors: (p.colors || []).join(", "), stock: p.stock === null || p.stock === undefined ? "" : String(p.stock) });

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Product name is required."); return; }
    const price = parseFloat(editing.price);
    if (!price || price <= 0) { toast.error("Enter a valid price."); return; }
    setSaving(true);
    try {
      const body = {
        name: editing.name, description: editing.description, price,
        image_file_id: editing.image_file_id,
        sizes: editing.sizes ? editing.sizes.split(",").map((s) => s.trim()).filter(Boolean) : [],
        colors: editing.colors ? editing.colors.split(",").map((s) => s.trim()).filter(Boolean) : [],
        stock: editing.stock === "" ? null : parseInt(editing.stock, 10),
        requires_shipping: editing.requires_shipping !== false,
        active: editing.active !== false,
        order: Number(editing.order) || 0,
        godaddy_url: (editing.godaddy_url || "").trim(),
      };
      if (editing.id) await api.put(`/admin/products/${editing.id}`, body);
      else await api.post("/admin/products", body);
      toast.success("Product saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/admin/products/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="store-manager">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <p className="text-slate-500 text-sm">Add and manage products sold in your store. Prices are in USD; leave Stock blank for unlimited.</p>
        <button data-testid="add-product-button" onClick={() => setEditing(blank)} className="inline-flex items-center gap-2 bg-[#D90429] text-white px-6 py-3 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#b0031f] transition-colors duration-200"><Plus className="w-4 h-4" /> Add Product</button>
      </div>

      {editing && (
        <div data-testid="product-form" className="bg-white border-2 border-[#0A1128] p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-display text-xl uppercase">{editing.id ? "Edit" : "New"} Product</h3>
            <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-[#D90429]"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><label className={labelCls}>Product Name</label><input data-testid="product-name" className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Description</label><textarea data-testid="product-description" rows={3} className={`${inputCls} resize-none`} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
            <div><label className={labelCls}>Price (USD)</label><input data-testid="product-price" type="number" step="0.01" className={inputCls} value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} /></div>
            <div><label className={labelCls}>Stock (blank = unlimited)</label><input data-testid="product-stock" type="number" className={inputCls} value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} /></div>
            <div><label className={labelCls}>Sizes (comma-separated)</label><input data-testid="product-sizes" placeholder="S, M, L, XL" className={inputCls} value={editing.sizes} onChange={(e) => setEditing({ ...editing, sizes: e.target.value })} /></div>
            <div><label className={labelCls}>Colors (comma-separated)</label><input data-testid="product-colors" placeholder="Black, Navy, White" className={inputCls} value={editing.colors} onChange={(e) => setEditing({ ...editing, colors: e.target.value })} /></div>
            <div className="sm:col-span-2 flex flex-wrap gap-6">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input data-testid="product-shipping-toggle" type="checkbox" checked={editing.requires_shipping !== false} onChange={(e) => setEditing({ ...editing, requires_shipping: e.target.checked })} className="w-4 h-4 accent-[#D90429]" />
                <span className="text-sm text-slate-700">Physical item (requires shipping)</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input data-testid="product-active-toggle" type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="w-4 h-4 accent-[#D90429]" />
                <span className="text-sm text-slate-700">Visible in store</span>
              </label>
            </div>
            <div><label className={labelCls}>Order</label><input type="number" className={inputCls} value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>GoDaddy Pay Link (price preset in GoDaddy)</label><input data-testid="product-godaddy-url" placeholder="https://…paylinks.godaddy.com/…" className={inputCls} value={editing.godaddy_url || ""} onChange={(e) => setEditing({ ...editing, godaddy_url: e.target.value })} /><p className="text-xs text-slate-400 mt-1">Create a pay link in GoDaddy with this product's price, then paste it here. Leave blank to use the default donate link.</p></div>
            <div className="sm:col-span-2"><ImageField value={editing.image_file_id} onChange={(id) => setEditing({ ...editing, image_file_id: id })} /></div>
          </div>
          <button data-testid="save-product-button" disabled={saving} onClick={save} className="mt-5 bg-[#0A1128] text-white px-8 py-3 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#1a2547] transition-colors duration-200 disabled:opacity-60 inline-flex items-center gap-2">{saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save Product"}</button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && <p className="text-slate-400 py-12 text-center bg-white border border-slate-200 sm:col-span-2 lg:col-span-3">No products yet.</p>}
        {items.map((p) => (
          <div key={p.id} data-testid="product-manage-row" className="bg-white border border-slate-200 overflow-hidden flex flex-col">
            <div className="h-32 bg-slate-100 flex items-center justify-center overflow-hidden relative">
              {p.image_file_id ? <img src={`${API}/public/files/${p.image_file_id}`} alt={p.name} className="w-full h-full object-cover" /> : <ImageIcon className="w-8 h-8 text-slate-300" />}
              {!p.active && <span className="absolute top-2 left-2 bg-slate-700 text-white text-[9px] uppercase px-2 py-0.5">Hidden</span>}
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <h4 className="font-display text-base uppercase leading-tight">{p.name}</h4>
              <p className="text-[#D90429] font-bold mt-0.5">${p.price.toFixed(2)}</p>
              <p className="text-xs text-slate-500 mt-1">{p.requires_shipping ? "Physical" : "Digital"}{p.stock !== null && p.stock !== undefined ? ` · ${p.stock} in stock` : " · unlimited"}</p>
              <div className="flex gap-3 mt-3">
                <button data-testid={`edit-product-${p.id}`} onClick={() => startEdit(p)} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#0A1128] hover:text-[#D90429]"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                <button data-testid={`delete-product-${p.id}`} onClick={() => remove(p.id)} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#D90429] hover:opacity-70"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function OrdersManager() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const fmtAddr = (o) => {
    const s = o.shipping_address;
    if (!s || !s.address) return null;
    const a = s.address;
    return [s.name, a.line1, a.line2, [a.city, a.state, a.postal_code].filter(Boolean).join(", "), a.country].filter(Boolean).join(", ");
  };

  return (
    <div data-testid="orders-manager">
      <p className="text-slate-500 text-sm mb-6">Paid store orders. Ship physical items to the address shown.</p>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
      ) : orders.length === 0 ? (
        <p className="text-slate-400 py-12 text-center bg-white border border-slate-200">No orders yet.</p>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} data-testid="order-row" className="bg-white border border-slate-200 p-5">
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div>
                  <p className="font-display text-lg uppercase inline-flex items-center gap-2"><Package className="w-4 h-4 text-[#D90429]" /> {o.customer_name || o.customer_email || "Customer"}</p>
                  {o.customer_email && <a href={`mailto:${o.customer_email}`} className="text-sm text-slate-500 hover:text-[#D90429] block">{o.customer_email}</a>}
                </div>
                <div className="text-right">
                  <p className="font-display text-xl text-[#0A1128]">${o.subtotal?.toFixed(2)}</p>
                  <p className="text-[10px] text-slate-400">{new Date(o.created_at).toLocaleString()}</p>
                </div>
              </div>
              <div className="mt-3 border-t border-slate-100 pt-3 space-y-1">
                {(o.items || []).map((it, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-slate-600">{it.name}{[it.size, it.color].filter(Boolean).length ? ` (${[it.size, it.color].filter(Boolean).join(", ")})` : ""} × {it.quantity}</span>
                    <span className="text-slate-500">${(it.price * it.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              {fmtAddr(o) && <p className="text-xs text-slate-500 mt-3 inline-flex items-start gap-1.5"><Truck className="w-3.5 h-3.5 text-[#D90429] mt-0.5 shrink-0" /> {fmtAddr(o)}</p>}
              {o.discount > 0 && <p className="text-xs text-green-600 mt-2 inline-flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> {o.promo_code}: -${o.discount.toFixed(2)} (total ${o.total?.toFixed(2)})</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export function PromoManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/promos").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const blank = { code: "", type: "percent", value: "", active: true, max_uses: "" };
  const startEdit = (p) => setEditing({ ...p, value: String(p.value), max_uses: p.max_uses === null || p.max_uses === undefined ? "" : String(p.max_uses) });

  const save = async () => {
    if (!editing.code.trim()) { toast.error("Code is required."); return; }
    const value = parseFloat(editing.value);
    if (!value || value <= 0) { toast.error("Enter a valid discount value."); return; }
    if (editing.type === "percent" && value > 100) { toast.error("Percent cannot exceed 100."); return; }
    setSaving(true);
    try {
      const body = {
        code: editing.code.trim().toUpperCase(), type: editing.type, value,
        active: editing.active !== false,
        max_uses: editing.max_uses === "" ? null : parseInt(editing.max_uses, 10),
      };
      if (editing.id) await api.put(`/admin/promos/${editing.id}`, body);
      else await api.post("/admin/promos", body);
      toast.success("Promo code saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this promo code?")) return;
    try { await api.delete(`/admin/promos/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="promo-manager">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <p className="text-slate-500 text-sm">Create discount codes (e.g. PATRIOT10) that customers apply in the cart at checkout.</p>
        <button data-testid="add-promo-button" onClick={() => setEditing(blank)} className="inline-flex items-center gap-2 bg-[#D90429] text-white px-6 py-3 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#b0031f] transition-colors duration-200"><Plus className="w-4 h-4" /> Add Code</button>
      </div>

      {editing && (
        <div data-testid="promo-form" className="bg-white border-2 border-[#0A1128] p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-display text-xl uppercase">{editing.id ? "Edit" : "New"} Promo Code</h3>
            <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-[#D90429]"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Code</label><input data-testid="promo-code" placeholder="PATRIOT10" className={`${inputCls} uppercase`} value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} /></div>
            <div><label className={labelCls}>Type</label><select data-testid="promo-type" className={inputCls} value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })}><option value="percent">Percent off (%)</option><option value="fixed">Fixed amount off ($)</option></select></div>
            <div><label className={labelCls}>{editing.type === "percent" ? "Percent (%)" : "Amount ($)"}</label><input data-testid="promo-value" type="number" step="0.01" className={inputCls} value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} /></div>
            <div><label className={labelCls}>Max Uses (blank = unlimited)</label><input data-testid="promo-max" type="number" className={inputCls} value={editing.max_uses} onChange={(e) => setEditing({ ...editing, max_uses: e.target.value })} /></div>
            <div className="sm:col-span-2">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input data-testid="promo-active-toggle" type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="w-4 h-4 accent-[#D90429]" />
                <span className="text-sm text-slate-700">Active (customers can use this code)</span>
              </label>
            </div>
          </div>
          <button data-testid="save-promo-button" disabled={saving} onClick={save} className="mt-5 bg-[#0A1128] text-white px-8 py-3 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#1a2547] transition-colors duration-200 disabled:opacity-60 inline-flex items-center gap-2">{saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save Code"}</button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && <p className="text-slate-400 py-12 text-center bg-white border border-slate-200 sm:col-span-2 lg:col-span-3">No promo codes yet.</p>}
        {items.map((p) => (
          <div key={p.id} data-testid="promo-manage-row" className="bg-white border border-slate-200 p-5">
            <div className="flex items-start justify-between">
              <div className="inline-flex items-center gap-2">
                <Tag className={`w-4 h-4 ${p.active ? "text-[#D90429]" : "text-slate-300"}`} />
                <span className="font-display text-lg uppercase tracking-wide">{p.code}</span>
              </div>
              {!p.active && <span className="text-[9px] uppercase bg-slate-200 text-slate-500 px-2 py-0.5">Inactive</span>}
            </div>
            <p className="text-sm text-[#0A1128] font-bold mt-1">{p.type === "percent" ? `${p.value}% off` : `$${p.value.toFixed(2)} off`}</p>
            <p className="text-xs text-slate-500 mt-0.5">Used {p.times_used || 0}{p.max_uses ? ` / ${p.max_uses}` : ""} time{(p.times_used || 0) === 1 ? "" : "s"}</p>
            <div className="flex gap-3 mt-3">
              <button data-testid={`edit-promo-${p.id}`} onClick={() => startEdit(p)} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#0A1128] hover:text-[#D90429]"><Pencil className="w-3.5 h-3.5" /> Edit</button>
              <button data-testid={`delete-promo-${p.id}`} onClick={() => remove(p.id)} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#D90429] hover:opacity-70"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


export function ForumManager() {
  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get("/forum/posts"),
        api.get("/admin/forum/comments"),
      ]);
      setPosts(p.data);
      setComments(c.data);
    } catch { toast.error("Failed to load forum data"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const when = (d) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

  const removePost = async (id) => {
    if (!window.confirm("Delete this post and all its comments?")) return;
    try { await api.delete(`/forum/posts/${id}`); toast.success("Post removed"); load(); }
    catch { toast.error("Delete failed"); }
  };
  const removeComment = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try { await api.delete(`/forum/comments/${id}`); toast.success("Comment removed"); load(); }
    catch { toast.error("Delete failed"); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>;

  return (
    <div data-testid="forum-manager" className="space-y-10">
      <div>
        <h3 className="font-display text-xl uppercase mb-1">Posts ({posts.length})</h3>
        <p className="text-slate-500 text-sm mb-4">Review member discussions and remove anything that violates community standards.</p>
        <div className="space-y-3">
          {posts.length === 0 && <p className="text-slate-400 py-8 text-center bg-white border border-slate-200">No forum posts yet.</p>}
          {posts.map((p) => (
            <div key={p.id} data-testid="forum-manage-post" className="bg-white border border-slate-200 p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#0A1128]">{p.title}</span>
                    {p.category && <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold text-[#D90429] bg-[#D90429]/10 px-2 py-0.5"><Tag className="w-3 h-3" /> {p.category}</span>}
                  </div>
                  <p className="text-sm text-slate-600 mt-1 line-clamp-2 whitespace-pre-wrap break-words">{p.body}</p>
                  <p className="text-xs text-slate-400 mt-2">
                    {p.user_name} · {when(p.created_at)} · {p.likes || 0} likes · {p.comment_count || 0} comments
                  </p>
                </div>
                <button data-testid={`admin-delete-post-${p.id}`} onClick={() => removePost(p.id)} className="shrink-0 inline-flex items-center gap-1 text-xs uppercase font-bold text-[#D90429] hover:opacity-70"><Trash2 className="w-4 h-4" /> Remove</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-display text-xl uppercase mb-1">Recent Comments ({comments.length})</h3>
        <p className="text-slate-500 text-sm mb-4">The latest comments across all posts.</p>
        <div className="space-y-2">
          {comments.length === 0 && <p className="text-slate-400 py-8 text-center bg-white border border-slate-200">No comments yet.</p>}
          {comments.map((c) => (
            <div key={c.id} data-testid="forum-manage-comment" className="bg-white border border-slate-200 p-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{c.text}</p>
                <p className="text-xs text-slate-400 mt-1.5">
                  <MessageCircle className="w-3 h-3 inline mr-1" />{c.user_name} · on "{c.post_title}" · {when(c.created_at)} · {c.likes || 0} likes
                </p>
              </div>
              <button data-testid={`admin-delete-comment-${c.id}`} onClick={() => removeComment(c.id)} className="shrink-0 inline-flex items-center gap-1 text-xs uppercase font-bold text-[#D90429] hover:opacity-70"><Trash2 className="w-4 h-4" /> Remove</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


const US_STATE_NAMES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming",
];

export function TestimonialsManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/testimonials").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await api.patch(`/admin/testimonials/${id}/approve`); toast.success("Approved — now live on the wall"); load(); }
    catch { toast.error("Approve failed"); }
  };

  const blank = { name: "", role: "", quote: "", image_file_id: null, order: items.length };

  const save = async () => {
    if (!editing.name.trim() || !editing.quote.trim()) { toast.error("Name and quote are required."); return; }
    setSaving(true);
    try {
      const body = { name: editing.name, role: editing.role, quote: editing.quote, image_file_id: editing.image_file_id, order: Number(editing.order) || 0 };
      if (editing.id) await api.put(`/admin/testimonials/${editing.id}`, body);
      else await api.post("/admin/testimonials", body);
      toast.success("Testimonial saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try { await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="testimonials-manager">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <p className="text-slate-500 text-sm">Add member and supporter quotes shown on the homepage.</p>
        <button data-testid="add-testimonial-button" onClick={() => setEditing(blank)} className="inline-flex items-center gap-2 bg-[#D90429] text-white px-6 py-3 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#b0031f] transition-colors duration-200"><Plus className="w-4 h-4" /> Add Testimonial</button>
      </div>

      {editing && (
        <div data-testid="testimonial-form" className="bg-white border-2 border-[#0A1128] p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-display text-xl uppercase">{editing.id ? "Edit" : "New"} Testimonial</h3>
            <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-[#D90429]"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Name</label><input data-testid="testimonial-name" className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
            <div><label className={labelCls}>Role / Title</label><input data-testid="testimonial-role" placeholder="e.g. Army Veteran, TX Chapter" className={inputCls} value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Quote</label><textarea data-testid="testimonial-quote" rows={3} className={`${inputCls} resize-none`} value={editing.quote} onChange={(e) => setEditing({ ...editing, quote: e.target.value })} /></div>
            <div><label className={labelCls}>Order</label><input type="number" className={inputCls} value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></div>
            <div className="sm:col-span-2"><ImageField value={editing.image_file_id} onChange={(id) => setEditing({ ...editing, image_file_id: id })} /></div>
          </div>
          <button data-testid="save-testimonial-button" disabled={saving} onClick={save} className="mt-5 bg-[#0A1128] text-white px-8 py-3 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#1a2547] transition-colors duration-200 disabled:opacity-60 inline-flex items-center gap-2">{saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save Testimonial"}</button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((t) => (
          <div key={t.id} data-testid="testimonial-manage-row" className={`bg-white border p-5 flex gap-4 ${t.status === "pending" ? "border-[#F4A261] ring-1 ring-[#F4A261]/40" : "border-slate-200"}`}>
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center overflow-hidden shrink-0">
              {t.image_file_id ? <img src={`${API}/public/files/${t.image_file_id}`} alt={t.name} className="w-full h-full object-cover" /> : <span className="font-display text-xl text-slate-400">{(t.name || "?").charAt(0).toUpperCase()}</span>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-display text-lg uppercase leading-tight">{t.name}</h4>
                {t.status === "pending" && <span data-testid={`testimonial-pending-${t.id}`} className="text-[10px] uppercase tracking-[0.12em] font-bold bg-[#F4A261] text-[#0A1128] px-2 py-0.5">Pending Review</span>}
              </div>
              {t.role && <p className="text-xs uppercase tracking-wide text-slate-400">{t.role}</p>}
              <p className="text-sm text-slate-500 line-clamp-2 mt-1 italic">"{t.quote}"</p>
              <div className="flex gap-3 mt-2 flex-wrap">
                {t.status === "pending" && (
                  <button data-testid={`approve-testimonial-${t.id}`} onClick={() => approve(t.id)} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-green-700 hover:opacity-70"><CheckCircle2 className="w-3.5 h-3.5" /> Approve</button>
                )}
                <button data-testid={`edit-testimonial-${t.id}`} onClick={() => setEditing(t)} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#0A1128] hover:text-[#D90429]"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                <button data-testid={`delete-testimonial-${t.id}`} onClick={() => remove(t.id)} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#D90429] hover:opacity-70"><Trash2 className="w-3.5 h-3.5" /> {t.status === "pending" ? "Reject" : "Delete"}</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ChaptersManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openFounders, setOpenFounders] = useState(null);
  const [founders, setFounders] = useState({});

  const load = () => api.get("/chapters").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const blank = { state: "Alabama", city: "", name: "", contact_email: "", contact_phone: "", link: "", status: "Active", order: items.length, founding_goal: 50 };

  const copyLink = (slug) => {
    const url = `${window.location.origin}/chapter/${slug}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Shareable link copied")).catch(() => toast.error("Copy failed"));
  };

  const toggleFounders = async (id) => {
    if (openFounders === id) { setOpenFounders(null); return; }
    setOpenFounders(id);
    try { const { data } = await api.get(`/admin/chapters/${id}/founding-members`); setFounders((f) => ({ ...f, [id]: data })); }
    catch { toast.error("Failed to load members"); }
  };

  const save = async () => {
    if (!editing.state.trim()) { toast.error("State is required."); return; }
    setSaving(true);
    try {
      const body = { state: editing.state, city: editing.city, name: editing.name, contact_email: editing.contact_email, contact_phone: editing.contact_phone, link: editing.link, status: editing.status, order: Number(editing.order) || 0, founding_goal: Number(editing.founding_goal) || 50 };
      if (editing.id) await api.put(`/admin/chapters/${editing.id}`, body);
      else await api.post("/admin/chapters", body);
      toast.success("Chapter saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this chapter?")) return;
    try { await api.delete(`/admin/chapters/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="chapters-manager">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <p className="text-slate-500 text-sm">Manage local chapters shown in the homepage Chapter Finder.</p>
        <button data-testid="add-chapter-button" onClick={() => setEditing(blank)} className="inline-flex items-center gap-2 bg-[#D90429] text-white px-6 py-3 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#b0031f] transition-colors duration-200"><Plus className="w-4 h-4" /> Add Chapter</button>
      </div>

      {editing && (
        <div data-testid="chapter-form" className="bg-white border-2 border-[#0A1128] p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-display text-xl uppercase">{editing.id ? "Edit" : "New"} Chapter</h3>
            <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-[#D90429]"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>State</label><select data-testid="chapter-state" className={inputCls} value={editing.state} onChange={(e) => setEditing({ ...editing, state: e.target.value })}>{US_STATE_NAMES.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
            <div><label className={labelCls}>City</label><input data-testid="chapter-city" placeholder="e.g. Austin" className={inputCls} value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Chapter Name (optional)</label><input data-testid="chapter-name" placeholder="e.g. Central Texas Chapter" className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
            <div><label className={labelCls}>Contact Email</label><input data-testid="chapter-email" className={inputCls} value={editing.contact_email} onChange={(e) => setEditing({ ...editing, contact_email: e.target.value })} /></div>
            <div><label className={labelCls}>Contact Phone</label><input data-testid="chapter-phone" className={inputCls} value={editing.contact_phone} onChange={(e) => setEditing({ ...editing, contact_phone: e.target.value })} /></div>
            <div><label className={labelCls}>Link (optional)</label><input data-testid="chapter-link" placeholder="https://…" className={inputCls} value={editing.link} onChange={(e) => setEditing({ ...editing, link: e.target.value })} /></div>
            <div><label className={labelCls}>Status</label><select data-testid="chapter-status" className={inputCls} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}><option value="Active">Active</option><option value="Forming">Forming</option></select></div>
            <div><label className={labelCls}>Founder Goal</label><input data-testid="chapter-founding-goal" type="number" min="1" placeholder="50" className={inputCls} value={editing.founding_goal ?? 50} onChange={(e) => setEditing({ ...editing, founding_goal: e.target.value })} /></div>
            <div><label className={labelCls}>Order</label><input type="number" className={inputCls} value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></div>
          </div>
          <button data-testid="save-chapter-button" disabled={saving} onClick={save} className="mt-5 bg-[#0A1128] text-white px-8 py-3 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#1a2547] transition-colors duration-200 disabled:opacity-60 inline-flex items-center gap-2">{saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save Chapter"}</button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((c) => (
          <div key={c.id} data-testid="chapter-manage-row" className="bg-white border border-slate-200 p-5">
            <div className="flex items-start justify-between gap-3">
              <h4 className="font-display text-lg uppercase leading-tight flex items-center gap-2"><MapPin className="w-4 h-4 text-[#D90429]" />{c.city ? `${c.city}, ` : ""}{c.state}</h4>
              <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 ${c.status === "Forming" ? "bg-[#F4A261]/20 text-[#b26a1f]" : "bg-[#D90429]/10 text-[#D90429]"}`}>{c.status || "Active"}</span>
            </div>
            {c.name && <p className="text-sm text-slate-600 mt-1">{c.name}</p>}
            <p className="text-xs text-slate-400 mt-1 truncate">{[c.contact_email, c.contact_phone].filter(Boolean).join(" · ")}</p>
            {c.slug && (
              <div className="mt-3 flex items-center gap-2 bg-slate-50 border border-slate-200 px-3 py-2">
                <Link2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span className="text-xs text-slate-500 truncate flex-1">/chapter/{c.slug}</span>
                <button data-testid={`copy-chapter-link-${c.id}`} onClick={() => copyLink(c.slug)} title="Copy shareable link" className="text-slate-500 hover:text-[#D90429]"><Copy className="w-3.5 h-3.5" /></button>
                <a data-testid={`open-chapter-page-${c.id}`} href={`/chapter/${c.slug}`} target="_blank" rel="noopener noreferrer" title="Open page" className="text-slate-500 hover:text-[#D90429]"><ExternalLink className="w-3.5 h-3.5" /></a>
              </div>
            )}
            <div className="flex gap-3 mt-3 flex-wrap items-center">
              <button data-testid={`edit-chapter-${c.id}`} onClick={() => setEditing(c)} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#0A1128] hover:text-[#D90429]"><Pencil className="w-3.5 h-3.5" /> Edit</button>
              <button data-testid={`delete-chapter-${c.id}`} onClick={() => remove(c.id)} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#D90429] hover:opacity-70"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              <button data-testid={`founders-toggle-${c.id}`} onClick={() => toggleFounders(c.id)} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-slate-600 hover:text-[#0A1128]"><Users className="w-3.5 h-3.5" /> Founding Members {openFounders === c.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}</button>
            </div>
            {openFounders === c.id && (
              <div data-testid={`founders-list-${c.id}`} className="mt-3 border-t border-slate-100 pt-3">
                {(founders[c.id] || []).length === 0 ? (
                  <p className="text-xs text-slate-400">No founding members yet. Share the link above to collect sign-ups.</p>
                ) : (
                  <div className="space-y-1.5">
                    <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">{founders[c.id].length} member{founders[c.id].length !== 1 ? "s" : ""}</p>
                    {founders[c.id].map((m) => (
                      <div key={m.id} className="text-xs text-slate-600 flex justify-between gap-2">
                        <span className="font-semibold">{m.name}</span>
                        <span className="text-slate-400 truncate">{m.email}{m.zip_code ? ` · ${m.zip_code}` : ""}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ChapterAppBadge({ status }) {
  const map = {
    pending: "bg-amber-100 text-amber-700",
    approved: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
  };
  return <span className={`inline-block px-2.5 py-1 text-xs uppercase tracking-wider font-semibold ${map[status] || map.pending}`}>{status}</span>;
}

export function ChapterApplicationsManager() {
  const [apps, setApps] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/admin/chapter-applications").then(({ data }) => setApps(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try { await api.patch(`/admin/chapter-applications/${id}`, { status }); toast.success(`Application ${status}`); load(); }
    catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this chapter application?")) return;
    try { await api.delete(`/admin/chapter-applications/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="chapter-applications-manager">
      <p className="text-slate-500 text-sm mb-6">Review requests from patriots who want to start a local chapter.</p>
      {apps.length === 0 ? (
        <p className="text-slate-400 py-12 text-center bg-white border border-slate-200">No chapter applications yet.</p>
      ) : (
        <div className="space-y-3">
          {apps.map((a) => (
            <div key={a.id} data-testid="chapter-application-row" className="bg-white border border-slate-200">
              <div className="flex items-center justify-between p-5 cursor-pointer" onClick={() => setExpanded(expanded === a.id ? null : a.id)}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-semibold text-lg">{a.full_name}</span>
                    <ChapterAppBadge status={a.status} />
                  </div>
                  <div className="text-sm text-slate-500 mt-1 truncate flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5 text-[#D90429]" />{a.city ? `${a.city}, ` : ""}{a.state} · {a.email}
                  </div>
                </div>
                {expanded === a.id ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </div>
              {expanded === a.id && (
                <div className="px-5 pb-5 border-t border-slate-100 pt-4 grid sm:grid-cols-2 gap-4 text-sm">
                  <div><span className="text-slate-400 uppercase text-xs tracking-wider block">Phone</span>{a.phone}</div>
                  <div><span className="text-slate-400 uppercase text-xs tracking-wider block">Occupation</span>{a.occupation || "—"}</div>
                  <div className="sm:col-span-2"><span className="text-slate-400 uppercase text-xs tracking-wider block">Experience</span>{a.experience || "—"}</div>
                  <div className="sm:col-span-2"><span className="text-slate-400 uppercase text-xs tracking-wider block">Reason</span>{a.reason}</div>
                  <div><span className="text-slate-400 uppercase text-xs tracking-wider block">Est. Supporters</span>{a.estimated_supporters || "—"}</div>
                  <div><span className="text-slate-400 uppercase text-xs tracking-wider block">Availability</span>{a.availability || "—"}</div>
                  <div className="sm:col-span-2"><span className="text-slate-400 uppercase text-xs tracking-wider block">Background Check OK</span>{a.background_check_ok ? "Yes" : "No"}</div>
                  {a.chapter_slug && (
                    <div className="sm:col-span-2 bg-green-50 border border-green-200 px-3 py-2 flex items-center gap-2 text-sm text-green-800" data-testid={`chapter-app-created-${a.id}`}>
                      <MapPin className="w-4 h-4 shrink-0" /> Chapter created —
                      <a href={`/chapter/${a.chapter_slug}`} target="_blank" rel="noopener noreferrer" className="font-semibold underline hover:opacity-70">view page</a>
                    </div>
                  )}
                  <div className="sm:col-span-2 flex gap-3 pt-2 flex-wrap">
                    <button data-testid={`chapter-approve-${a.id}`} onClick={() => updateStatus(a.id, "approved")} className="flex items-center gap-2 bg-green-600 text-white px-5 py-2.5 uppercase tracking-wider text-xs font-bold hover:bg-green-700 transition-colors duration-200"><CheckCircle2 className="w-4 h-4" /> Approve</button>
                    <button data-testid={`chapter-reject-${a.id}`} onClick={() => updateStatus(a.id, "rejected")} className="flex items-center gap-2 bg-[#0A1128] text-white px-5 py-2.5 uppercase tracking-wider text-xs font-bold hover:bg-[#1a2547] transition-colors duration-200"><XCircle className="w-4 h-4" /> Reject</button>
                    <button data-testid={`chapter-app-delete-${a.id}`} onClick={() => remove(a.id)} className="flex items-center gap-2 text-[#D90429] px-3 py-2.5 uppercase tracking-wider text-xs font-bold hover:opacity-70 transition-opacity"><Trash2 className="w-4 h-4" /> Delete</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export function LeaderDirectory() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({ email: "", name: "" });
  const [savingLeader, setSavingLeader] = useState(false);

  const load = () => api.get("/admin/chapters/directory").then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const startEdit = (r) => { setEditId(r.id); setEdit({ email: r.leader_email || "", name: r.leader_name || "" }); };

  const saveLeader = async (id) => {
    setSavingLeader(true);
    try {
      await api.put(`/admin/chapters/${id}/leader`, edit);
      toast.success(edit.email.trim() ? "Leader assigned — they'll get an email to set up." : "Leader unassigned");
      setEditId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update leader");
    } finally { setSavingLeader(false); }
  };

  const clearLeader = async (id) => {
    if (!window.confirm("Remove the leader from this chapter?")) return;
    try { await api.put(`/admin/chapters/${id}/leader`, { email: "", name: "" }); toast.success("Leader unassigned"); load(); }
    catch { toast.error("Failed"); }
  };

  const filtered = rows.filter((r) => {
    const s = q.toLowerCase();
    return !s || [r.name, r.state, r.city, r.leader_name, r.leader_email, r.contact_email].some((v) => (v || "").toLowerCase().includes(s));
  });

  const totalFounders = rows.reduce((s, r) => s + (r.founder_count || 0), 0);

  return (
    <div data-testid="leader-directory">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <p className="text-slate-500 text-sm">All chapters at a glance — leaders, contacts, and founder counts.</p>
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-xs uppercase tracking-wider text-slate-400">{rows.length} chapters · {totalFounders} founders</span>
          <input data-testid="directory-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search chapters or leaders…" className="border border-slate-300 px-4 py-2 text-sm focus:outline-none focus:border-[#D90429]" />
        </div>
      </div>

      {loading ? (
        <p className="text-slate-400 py-12 text-center">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-slate-400 py-12 text-center bg-white border border-slate-200">No chapters found.</p>
      ) : (
        <div className="overflow-x-auto bg-white border border-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#0A1128] text-white text-left text-xs uppercase tracking-wider">
                <th className="px-4 py-3">Chapter</th>
                <th className="px-4 py-3">Leader</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-center">Founders</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const pct = Math.min(100, Math.round((r.founder_count / (r.founding_goal || 50)) * 100));
                return (
                  <tr key={r.id} data-testid="directory-row" className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#0A1128] flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#D90429]" />{r.name || `${r.state} Chapter`}</div>
                      <div className="text-xs text-slate-400">{r.city ? `${r.city}, ` : ""}{r.state}</div>
                    </td>
                    <td className="px-4 py-3">
                      {editId === r.id ? (
                        <div className="space-y-2 min-w-[220px]" data-testid={`leader-edit-${r.id}`}>
                          <input data-testid={`leader-email-input-${r.id}`} value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} placeholder="leader@email.com" className="w-full border border-slate-300 px-3 py-1.5 text-xs focus:outline-none focus:border-[#D90429]" />
                          <input data-testid={`leader-name-input-${r.id}`} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Leader name (optional)" className="w-full border border-slate-300 px-3 py-1.5 text-xs focus:outline-none focus:border-[#D90429]" />
                          <div className="flex gap-2">
                            <button data-testid={`leader-save-${r.id}`} disabled={savingLeader} onClick={() => saveLeader(r.id)} className="bg-[#0A1128] text-white px-3 py-1.5 text-[10px] uppercase font-bold hover:bg-[#1a2547] disabled:opacity-60">{savingLeader ? "Saving…" : "Save"}</button>
                            <button data-testid={`leader-cancel-${r.id}`} onClick={() => setEditId(null)} className="text-slate-400 px-2 py-1.5 text-[10px] uppercase font-bold hover:text-[#0A1128]">Cancel</button>
                          </div>
                        </div>
                      ) : r.leader_email ? (
                        <div>
                          <div className="font-medium">{r.leader_name || "—"}</div>
                          <div className="text-xs text-slate-400">{r.leader_email}</div>
                          <div className="flex gap-2 mt-1">
                            <button data-testid={`leader-change-${r.id}`} onClick={() => startEdit(r)} className="text-[10px] uppercase font-bold text-[#0A1128] hover:text-[#D90429]">Change</button>
                            <button data-testid={`leader-clear-${r.id}`} onClick={() => clearLeader(r.id)} className="text-[10px] uppercase font-bold text-[#D90429] hover:opacity-70">Remove</button>
                          </div>
                        </div>
                      ) : (
                        <button data-testid={`leader-assign-${r.id}`} onClick={() => startEdit(r)} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#D90429] hover:opacity-70"><Plus className="w-3.5 h-3.5" /> Assign Leader</button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {r.contact_email && <div>{r.contact_email}</div>}
                      {r.contact_phone && <div>{r.contact_phone}</div>}
                      {!r.contact_email && !r.contact_phone && "—"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 ${r.status === "Forming" ? "bg-[#F4A261]/20 text-[#b26a1f]" : "bg-[#D90429]/10 text-[#D90429]"}`}>{r.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 justify-center">
                        <span className="font-display text-lg text-[#0A1128] w-14 text-right">{r.founder_count}/{r.founding_goal || 50}</span>
                        <div className="w-16 h-2 bg-slate-100 overflow-hidden hidden sm:block">
                          <div className="h-full bg-gradient-to-r from-[#D90429] to-[#F4A261]" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {r.slug && <a href={`/chapter/${r.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`directory-view-${r.id}`} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#0A1128] hover:text-[#D90429]"><ExternalLink className="w-3.5 h-3.5" /> Page</a>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

